import React from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { FormRender } from './Form'
import { FieldSettings, JSONContent } from './Form/types'
import { baseURL } from './baseUrl'
import {
  createSession,
  getSession,
  partialSubmit,
  submitSession,
  saveSessionToStorage,
  getSessionFromStorage,
  removeSessionFromStorage,
  FormData,
} from './api/session'
// import { ThemeOptions } from '@mui/material'

export default function App() {
  const [form, setForm] = React.useState<FormData | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<Error | null>(null)
  const [sessionId, setSessionId] = React.useState<string | null>(null)
  const [initialValues, setInitialValues] = React.useState<Record<
    string,
    any
  > | null>(null)
  const [showRestoreDialog, setShowRestoreDialog] = React.useState(false)
  const [pendingRestoreData, setPendingRestoreData] = React.useState<{
    form: FormData
    sessionId: string
    lastPartialSubmitValues: Record<string, any>
  } | null>(null)
  const formIdRef = React.useRef<string | null>(null)

  // Ref для debounce partial submit
  const partialSubmitTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const lastPartialSubmitValuesRef = React.useRef<string>('')

  if (!formIdRef.current) {
    formIdRef.current = new URLSearchParams(window.location.search).get(
      'formId'
    )
  }

  // Загрузка формы и инициализация/восстановление сессии
  React.useEffect(() => {
    let isMounted = true
    const formId = formIdRef.current

    if (!formId) {
      setError(new Error('Form ID is missing in the URL query string'))
      setIsLoading(false)

      return () => {
        isMounted = false
      }
    }

    async function initializeFormAndSession() {
      let dialogShown = false

      try {
        // Проверяем, есть ли сохраненная сессия в localStorage
        const storedSession = getSessionFromStorage(formId!)

        if (storedSession) {
          // Пытаемся восстановить существующую сессию
          try {
            const sessionResponse = await getSession(
              formId!,
              storedSession.sessionId
            )

            if (isMounted) {
              // Проверяем статус сессии - если complete, создаем новую
              if (sessionResponse.data.session.status === 'complete') {
                console.log('Session is complete, creating new one')
                removeSessionFromStorage(formId!)
                await createNewSession(formId!, isMounted)

                return
              }

              // Проверяем наличие сохраненных данных формы
              const lastPartialValues =
                sessionResponse.data.lastPartialSubmitValues
              const hasPartialValues =
                lastPartialValues &&
                typeof lastPartialValues === 'object' &&
                Object.keys(lastPartialValues).length > 0

              if (hasPartialValues) {
                // Есть сохраненные данные - показываем диалог
                setPendingRestoreData({
                  form: sessionResponse.data.form,
                  sessionId: storedSession.sessionId,
                  lastPartialSubmitValues: lastPartialValues,
                })
                setShowRestoreDialog(true)
                dialogShown = true
                // Не устанавливаем isLoading в false - диалог управляет этим
              } else {
                // Нет сохраненных данных - продолжаем как обычно
                setForm(sessionResponse.data.form)
                setSessionId(storedSession.sessionId)

                // Восстанавливаем значения из сессии, если они есть
                if (sessionResponse.data.session.values) {
                  setInitialValues(sessionResponse.data.session.values)
                }
              }
            }
          } catch (sessionError) {
            // Сессия недоступна (устарела или удалена), создаем новую
            console.warn(
              'Failed to restore session, creating new one:',
              sessionError
            )
            removeSessionFromStorage(formId!)
            await createNewSession(formId!, isMounted)
          }
        } else {
          // Нет сохраненной сессии, создаем новую
          await createNewSession(formId!, isMounted)
        }
      } catch (err) {
        if (isMounted) {
          setError(err as Error)
        }
      } finally {
        // Не скрываем loading если показан диалог восстановления - 
        // он сам управляет состоянием загрузки
        if (isMounted && !dialogShown) {
          setIsLoading(false)
        }
      }
    }

    async function createNewSession(formId: string, isMounted: boolean) {
      // Создаем сессию - она возвращает и данные формы
      const sessionResponse = await createSession(formId)

      if (isMounted) {
        const sessionData = sessionResponse.data

        // Сохраняем sessionId в localStorage
        saveSessionToStorage(formId, sessionData.session.id)
        setSessionId(sessionData.session.id)

        // Используем данные формы из ответа createSession
        setForm(sessionData.form)
      }
    }

    initializeFormAndSession()

    return () => {
      isMounted = false
      // Очищаем debounce timeout при unmount
      if (partialSubmitTimeoutRef.current) {
        clearTimeout(partialSubmitTimeoutRef.current)
      }
    }
  }, [])

  // Обработчик частичного сохранения (debounced)
  const handlePartialSubmit = React.useCallback(
    (values: Record<string, any>) => {
      const formId = formIdRef.current
      if (!formId || !sessionId) {
        return
      }

      // Проверяем, изменились ли значения
      const valuesString = JSON.stringify(values)
      if (valuesString === lastPartialSubmitValuesRef.current) {
        return
      }

      // Очищаем предыдущий timeout если есть
      if (partialSubmitTimeoutRef.current) {
        clearTimeout(partialSubmitTimeoutRef.current)
      }

      // Устанавливаем новый timeout для debounce (500ms)
      partialSubmitTimeoutRef.current = setTimeout(() => {
        lastPartialSubmitValuesRef.current = valuesString

        partialSubmit(formId, sessionId, values).catch((err) => {
          console.error('Failed to save partial data:', err)
        })
      }, 500)
    },
    [sessionId]
  )

  // Обработчик принятия восстановления данных формы
  const handleAcceptRestore = React.useCallback(() => {
    if (pendingRestoreData) {
      setForm(pendingRestoreData.form)
      setSessionId(pendingRestoreData.sessionId)
      setInitialValues(pendingRestoreData.lastPartialSubmitValues)
    }
    setShowRestoreDialog(false)
    setPendingRestoreData(null)
    setIsLoading(false)
  }, [pendingRestoreData])

  // Обработчик отказа от восстановления - создаем новую сессию
  const handleDeclineRestore = React.useCallback(async () => {
    setShowRestoreDialog(false)
    setPendingRestoreData(null)

    const formId = formIdRef.current
    if (formId) {
      try {
        removeSessionFromStorage(formId)
        const sessionResponse = await createSession(formId)
        const sessionData = sessionResponse.data

        saveSessionToStorage(formId, sessionData.session.id)
        setSessionId(sessionData.session.id)
        setForm(sessionData.form)
      } catch (err) {
        setError(err as Error)
      }
    }
    setIsLoading(false)
  }, [])

  // Обработчик финального submit
  const handleSubmit = React.useCallback(
    (values: unknown) => {
      const formId = formIdRef.current
      if (!formId) {
        return Promise.reject(
          new Error('Form ID is missing in the URL query string')
        )
      }

      // Если есть sessionId, используем новый endpoint
      if (sessionId) {
        return submitSession(formId, sessionId, values as Record<string, any>)
          .then((data) => {
            // Очищаем сессию из localStorage после успешного submit
            removeSessionFromStorage(formId)

            if (window.parent && window.parent !== window) {
              window.parent.postMessage(
                {
                  type: 'FORM_SUBMIT_SUCCESS',
                  formId: formId,
                  data: data,
                },
                '*'
              )
            }
          })
          .catch((submitError) => {
            console.error('Ошибка:', submitError)
            throw submitError
          })
      }

      // Fallback на старый endpoint если нет sessionId
      return fetch(`${baseURL}/form/${formId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          values,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (window.parent && window.parent !== window) {
            window.parent.postMessage(
              {
                type: 'FORM_SUBMIT_SUCCESS',
                formId: formId,
                data: data,
              },
              '*'
            )
          }
        })
        .catch((submitError) => {
          console.error('Ошибка:', submitError)
          throw submitError
        })
    },
    [sessionId]
  )

  const layout = React.useMemo(() => {
    return {
      contianerStyles: form?.settings.layout?.contianer_styles || '',
      bodyStyles: form?.settings.layout?.body_styles || '',
      fields: JSON.parse(form?.settings.layout?.fields || '{}') as Record<
        string,
        FieldSettings
      >,
      theme: form?.settings.layout?.theme,
    }
  }, [form])

  const content = React.useMemo(() => {
    return JSON.parse(form?.settings.content_json || '{}') as JSONContent
  }, [form])

  const validationScheme = React.useMemo(() => {
    return form?.validation_scheme // JSON.parse(form?.validation_scheme || '{}') as JSONContent
  }, [form])

  // Показываем диалог восстановления данных даже во время загрузки
  if (showRestoreDialog) {
    return (
      <Dialog
        open={showRestoreDialog}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            minWidth: '400px',
            maxWidth: '480px',
            p: 0,
          },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          {/* Header with icon and title */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: '#2DD4A8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HelpOutlineIcon sx={{ color: 'white', fontSize: 32 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: '#1a1a1a' }}
              >
                Restore Form Data
              </Typography>
            </Box>
          </Box>

          {/* Message */}
          <Typography sx={{ color: '#1a1a1a', fontSize: '16px', mb: 1 }}>
            You already have saved form data. Do you want to continue where you
            left off?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button
            onClick={handleDeclineRestore}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              borderColor: '#1a1a1a',
              color: '#1a1a1a',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              '&:hover': {
                borderColor: '#1a1a1a',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Start Over
          </Button>
          <Button
            onClick={handleAcceptRestore}
            variant="contained"
            autoFocus
            sx={{
              borderRadius: '8px',
              backgroundColor: '#2DD4A8',
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              '&:hover': {
                backgroundColor: '#25b892',
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  if (isLoading) return null
  if (error) return <div>{error.message}</div>
  if (!form) return null

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormRender
        content={content}
        layout={layout}
        validationScheme={validationScheme}
        onSubmit={handleSubmit}
        sessionId={sessionId}
        onPartialSubmit={handlePartialSubmit}
        initialValues={initialValues}
      />
    </LocalizationProvider>
  )
}
